import { ColumnDefinition, EntityInterface } from "./Table";

/**
 * Application is an abstracted interface that contains useful helper functions when dealing
 * with applications
 */
export class Application implements EntityInterface {
  /**
   * Construct the application from an interface with sanitized fields. Strings
   * are trimmed or are empty strings if the field is null. Empty application ids
   * are turned into zeros
   *
   * @param intf The interface to construct the application from
   */
  constructor(
    public id: string,
    public businessName: string = '',
    public businessDBA: string = '',
    public phone: string = '',
    public email: string = '',
    public accountId: number = 0,
    public appliedOn: string = '',
    public status: string = '',
    public name: string = '',
    public sentBy: string = ''
  ) { }

  /**
   * Determine if two applications are equal
   *
   * @param app The other application to check
   * @returns True if applications are equal
   */
  public equals(app: Application): boolean {
    return this.id === app.id &&
      this.businessName === app.businessName &&
      this.businessDBA === app.businessDBA &&
      this.phone === app.phone &&
      this.email === app.email &&
      this.accountId === app.accountId &&
      this.appliedOn === app.appliedOn &&
      this.status === app.status &&
      this.sentBy === app.sentBy;
  }

  /**
   * Return a copy of this application
   */
  public copyOf(): Application {
    return new Application(
      this.id,
      this.businessName,
      this.businessDBA,
      this.phone,
      this.email,
      this.accountId,
      this.appliedOn,
      this.status,
      this.name,
      this.sentBy
    )
  }

  /**
   * Return the application as a string representation, used for filtering/searching
   *
   * We use a unicode full block as a delimeter, so the only string matches that
   * return are from our fields (unless the user is typing the unicode full block
   * character like a maniac)
   */
  public toString(defs: ColumnDefinition<Application>[]): string {
    let str = "";
    for (let col of defs) {
      if (col.toStringTransform != undefined) {
        str += col.toStringTransform((this as any)[col.field]) + "█";
      } else {
        str += (this as any)[col.field];
      }
    }

    return str.toLowerCase();
  }

}

/**
* ApplicationDto is an interface that conforms to the API response for applications. This
* is used to safely cast data from the backend as well as sanitize data in the frontend before
* sending it to the backend
*/
export interface ApplicationDto {
  applicationId: string;
  business_name: string;
  business_dba: string;
  name: string;
  phoneNumber: string;
  email: string;
  account_id: number;
  applied_on: string;
  status: string; // TODO: can change to more strict type
  sentBy: string;
}

/**
 * ApplicationMapper is a service that maps between the Application model and the ApplicationDto
 * interface that is used to interact with the backend API.
 */
export class ApplicationMapper {

  /**
   * toDto takes an Application and returns an ApplicationDto
   * 
   * @param application the Application to convert
   * @returns an ApplicationDto
   */
  static toDto(model: Application): ApplicationDto {
    throw new Error('Method not implemented.');
  }

  /**
   * toModel takes an ApplicationDto and returns an Application model
   * 
   * @param dto the ApplicationDto to convert
   * @returns an Application model
   */
  static toModel(dto: ApplicationDto): Application {
    return new Application(
      dto.applicationId.trim(), // applicationId
      dto.business_name.trim(), // businessName
      dto.business_dba.trim(), // businessDBA
      dto.phoneNumber.trim(), // phone
      dto.email.trim(), // email
      dto.account_id, // accountId
      dto.applied_on.trim(), // appliedOn
      dto.status.trim(), // status
      dto.name, // name
      dto.sentBy.trim() // sentBy
    );
  }
}